<template>
  <div class="suggested-location-row" :class="{ 'suggested-location-row--removed': props.item.state === 'removed', 'suggested-location-row--additional': props.item.state === 'additional' }">
    <LocationAvatar :src="props.item.icon" :name="props.item.name" :size="40" />

    <div class="suggested-location-row__copy">
      <div class="suggested-location-row__name-row">
        <span class="suggested-location-row__name">{{ props.item.name }}</span>
        <a v-if="websiteUrl" class="suggested-location-row__website" :href="websiteUrl" target="_blank" rel="noopener noreferrer" :aria-label="`Visit ${props.item.name}'s website (opens in a new tab)`" title="Visit website" @click.stop>
          <IconEmbedded name="external-link_2-5" :size="14" color="currentColor" />
        </a>
        <LocationCategoryIcon :option="props.item.categoryOption" :size="24" />
        <span v-if="props.item.state === 'added'" class="suggested-location-row__tag">Added</span>
      </div>
      <div class="suggested-location-row__meta">
        <LocationSocialHandles :location="props.item.location" />
        <a v-if="props.item.city" class="suggested-location-row__city suggested-location-row__map" :href="googleMapsUrl" target="_blank" rel="noopener noreferrer" :aria-label="`View ${props.item.name} in ${props.item.city} on Google Maps (opens in a new tab)`" title="View on Google Maps" @click.stop>
          <IconEmbedded name="map-pin_2" :size="14" color="#667085" />
          {{ props.item.city }}
        </a>
        <span v-else-if="props.item.timing" class="suggested-location-row__city">{{ props.item.timing }}</span>
      </div>
      <div v-if="props.showDebug" class="suggested-location-row__debug" :title="props.item.suggestion?.excludeFromIndex.aiReason">{{ debugText }}</div>
    </div>

    <div class="suggested-location-row__actions">
      <template v-if="props.item.state === 'removed'">
        <SrpButton color="gray" fill="outlined" size="small" :isDisabled="props.isDisabled" @click="emit('restore')">Restore</SrpButton>
      </template>
      <template v-else-if="props.item.state === 'additional'">
        <SrpButton tag="button" type="button" class="suggested-location-row__remove" color="gray" fill="outlined" size="small" :isDisabled="props.isDisabled" :disabled="props.isDisabled" @click="emit('remove')">Remove</SrpButton>
        <SrpButton tag="button" type="button" color="teal" fill="outlined" size="small" :isDisabled="props.isDisabled" :disabled="props.isDisabled" @click="emit('add')">Include</SrpButton>
      </template>
      <template v-else>
        <SrpButton color="gray" fill="outlined" size="small" :isDisabled="props.isDisabled" @click="emit('edit')">Edit</SrpButton>
        <SrpButton v-if="props.item.state === 'suggested'" class="suggested-location-row__remove" color="gray" fill="outlined" size="small" :isDisabled="props.isDisabled" @click="emit('remove')">
          Remove
        </SrpButton>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import LocationAvatar from "@components/SocialLocations/LocationAvatar.vue";
import LocationCategoryIcon from "@components/SocialLocations/LocationCategoryIcon.vue";
import LocationSocialHandles from "@components/SocialLocations/LocationSocialHandles.vue";
import type { LocationCategoryOption } from "@logic/LocationCategories";
import type { LocationData } from "@contracts/socialLocations";
import type { NearbySuggestion } from "@contracts/nearbyLocations";

/** One line in the list: a suggestion still to accept, one the user removed, or a Location that is already saved. */
export interface SuggestedLocationItem {
  key: string;
  state: "suggested" | "removed" | "added" | "additional";
  name: string;
  icon?: string | null;
  categoryOption: LocationCategoryOption;
  city: string;
  /** Events have no city; show when they happen instead. */
  timing?: string;
  /** For the shared social-handles display. */
  location: LocationData;
  suggestion?: NearbySuggestion;
  /** The saved Location, for "added" rows. */
  savedLocation?: LocationData;
}

const props = withDefaults(defineProps<{ item: SuggestedLocationItem; isDisabled?: boolean; showDebug?: boolean }>(), { isDisabled: false, showDebug: false });
const websiteUrl = computed(() => {
  const website = (props.item.suggestion?.websiteUri || props.item.location.website || "").trim();
  if (!website) return undefined;
  try {
    const url = new URL(website.startsWith("//") ? `https:${website}` : /^[a-z][a-z\d+.-]*:/i.test(website) ? website : `https://${website}`);
    return ["http:", "https:"].includes(url.protocol) ? url.href : undefined;
  } catch {
    return undefined;
  }
});
const googleMapsUrl = computed(() => {
  const placeId = props.item.suggestion?.googlePlaceId || props.item.location.googlePlaceId;
  const address = props.item.suggestion?.address || props.item.location.address;
  const locality = [props.item.city, props.item.location.state].filter(Boolean).join(", ");
  const query = [props.item.name, address || locality].filter(Boolean).join(", ");
  const params = new URLSearchParams({ api: "1", query });
  if (placeId) params.set("query_place_id", placeId);
  return `https://www.google.com/maps/search/?${params}`;
});
const debugText = computed(() => {
  const suggestion = props.item.suggestion;
  if (!suggestion) return "Saved location · no AI rating";
  return [
    suggestion.aiScore != null ? `AI score ${suggestion.aiScore}/100` : "AI score —",
    suggestion.aiRank != null ? `AI rank #${suggestion.aiRank}` : "AI rank —",
  ].join(" · ");
});
const emit = defineEmits<{
  (e: "edit"): void;
  (e: "remove"): void;
  (e: "restore"): void;
  (e: "add"): void;
}>();
</script>

<style scoped lang="scss">
@import "@/scss/variables.scss";

// Suggested location row =====================================================
.suggested-location-row {
  padding: 14px 16px;
  gap: 12px;
  display: flex;
  align-items: center;
  border-top: 1px solid #e7e9ed;
  background: #fff;

  &--removed {
    opacity: 0.7;
  }

  &--additional {
    padding: 18px 0;
    background: transparent;
    border-top-color: #d8dfea;
  }

  &__copy {
    min-width: 0;
    flex: 1;
    gap: 3px;
    display: grid;
  }

  &__name-row {
    gap: 8px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }

  &__name {
    min-width: 0;
    color: #102f41;
    font: 600 15px/20px $font-family;
    overflow-wrap: anywhere;
  }

  &__tag {
    padding: 2px 8px;
    border-radius: 100px;
    background: #e9f5f4;
    color: #05696b;
    font: 500 12px/16px sans-serif;
  }

  &__meta {
    min-height: 18px;
    gap: 4px 12px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    color: #667085;
    font-size: 13px;
  }

  &__city {
    gap: 4px;
    display: inline-flex;
    align-items: center;
  }

  &__website {
    width: 20px;
    height: 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__website,
  &__map {
    color: #667085;
    text-decoration: none;

    &:hover {
      color: #087f82;
    }

    &:focus-visible {
      outline: 2px solid #087f82;
      outline-offset: 2px;
      border-radius: 3px;
    }
  }

  &__map:hover {
    text-decoration: underline;
  }

  &__debug {
    margin-top: 3px;
    color: #667085;
    font: 400 11px/16px $font-family;
    overflow-wrap: anywhere;
  }

  &__actions {
    gap: 8px;
    display: flex;
    flex-shrink: 0;
  }

  &__remove {
    color: #806468;
  }
}

// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .suggested-location-row {
    flex-wrap: wrap;

    &__copy {
      flex-basis: calc(100% - 60px);
    }

    &__actions {
      width: 100%;
      justify-content: flex-end;
    }
  }
}
</style>
